// src/pages/Services.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import GalleryCarousel from '../components/GalleryCarousel';
import QuickConsult from '../components/QuickConsult';
import ServicesGrid from '../components/ServicesGrid';
import { PublicAPI } from '../api/public';

const Container = styled.div`
  .container { 
    width: min(1400px, 96vw); 
    margin: 0 auto; 
    padding: 0 18px; 
  }
`;

const Section = styled.section`
  margin: 32px 0;
  
  &:first-child {
    margin-top: 20px;
  }
`;

const GallerySection = styled(Section)`
  .container {
    padding: 0;
  }
`;

const Content = styled.div`
  max-width: 1000px;
  margin: 0 auto; /* Центрируем контент */
  
  h1 {
    font-size: clamp(28px, 4vw, 48px);
    margin-bottom: 20px;
    color: var(--fg);
    font-weight: 800;
    text-align: center;
  }
  
  h2 {
    font-size: clamp(24px, 3.5vw, 32px);
    margin: 32px 0 20px 0;
    color: var(--fg);
    font-weight: 700;
    text-align: center;
  }
  
  p {
    font-size: 18px;
    line-height: 1.6;
    color: var(--fg);
    margin-bottom: 20px;
    text-align: center;
  }
`;

export default function Services() {
  const { t, i18n } = useTranslation();
  const [services, setServices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Загрузка услуг из API
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await PublicAPI.services({
          locale: i18n.language || 'ru',
        });
        const list = Array.isArray(res?.services) ? res.services : 
                     Array.isArray(res?.items) ? res.items : 
                     Array.isArray(res) ? res : [];
        
        if (!cancelled) {
          setServices(list);
        }
      } catch (error) {
        console.error('Failed to load services:', error);
        if (!cancelled) {
          setServices([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language]);

  // Изображения для слайдера услуг
  const galleryImages = [
    'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=600&fit=crop',
    'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
    'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1200&h=600&fit=crop',
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop',
  ];

  return (
    <Container>
      {/* 1. Слайдер с фотками */}
      <GallerySection>
        <div className="container">
          <GalleryCarousel 
            images={galleryImages} 
            ratio="21/9" 
            altPrefix="Услуги ASTORIUS"
          />
        </div>
      </GallerySection>

      {/* 2. Текст */}
      <Section>
        <div className="container">
          <Content>
            <h1>{t('services', 'Наши услуги')}</h1>
            <p>
              {t('services.description', 'Полный цикл услуг в сфере недвижимости: от подбора и сделки до управления и дизайна. Мы предоставляем комплексные решения для частных клиентов и инвесторов, обеспечивая профессиональное сопровождение на каждом этапе.')}
            </p>

            <h2>{t('services.directions', 'Направления деятельности')}</h2>
            
            {/* Используем новый компонент ServicesGrid */}
            <ServicesGrid 
              services={services}
              loading={loading}
              emptyMessage={t('services.noServices', 'Услуги временно недоступны')}
            />
          </Content>
        </div>
      </Section>

      {/* 3. Форма консультации */}
      <Section>
        <div className="container">
          <Content>
            <h2 style={{ marginBottom: '20px' }}>
              {t('services.consultation', 'Получить консультацию')}
            </h2>
            <QuickConsult />
          </Content>
        </div>
      </Section>
    </Container>
  );
}
